/**
 * Global reports list — every report across every project, newest first.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { LineChart, Search } from 'lucide-react';
import type { Project, Report } from '@seo/types';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusPill } from '@/components/status-pill';
import { useReports } from '@/features/reports/use-reports';
import { useProjects } from '@/features/projects/use-projects';
import { ReportCreateButton } from '@/features/reports/components/report-create-button';
import { relTime } from '@/lib/format';

export function ReportsRoute(): JSX.Element {
  const navigate = useNavigate();
  const reports = useReports();
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];
  const [query, setQuery] = useState('');

  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [projects.data]);

  const rows = useMemo(() => {
    const items = reports.data?.items ?? [];
    const q = query.trim().toLowerCase();
    const sorted = [...items].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    if (!q) return sorted;
    return sorted.filter((r) => {
      const p = projectsById.get(r.project_id);
      return (
        r.cadence.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        (p?.name.toLowerCase().includes(q) ?? false)
      );
    });
  }, [reports.data, projectsById, query]);

  return (
    <Page>
      <PageHeader
        title="Performance reports"
        description="See how your SEO is doing — traffic, rankings, and anything unusual we spotted this period."
        actions={
          <ReportCreateButton projects={availableProjects} />
        }
      />

      <PageToolbar>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by frequency, status, or project..."
            className="h-9 pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rows.length} of {reports.data?.items.length ?? 0}
        </span>
      </PageToolbar>

      {reports.error ? <ErrorBanner error={reports.error} /> : null}

      <DataTable<Report>
        onRowClick={(r) =>
          void navigate({ to: '/reports/$reportId', params: { reportId: r.id } })
        }
        loading={reports.isLoading}
        rows={rows}
        rowKey={(r) => r.id}
        columns={[
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <StatusPill status={r.status} />,
            className: 'w-32',
          },
          {
            key: 'period',
            header: 'Period',
            cell: (r) => (
              <span className="text-sm font-medium">
                {r.period_start} → {r.period_end}
              </span>
            ),
          },
          {
            key: 'project',
            header: 'Project',
            cell: (r) => {
              const p = projectsById.get(r.project_id);
              return (
                <span className="text-sm text-muted-foreground">
                  {p?.name ?? r.project_id.slice(0, 8)}
                </span>
              );
            },
          },
          {
            key: 'cadence',
            header: 'How often',
            cell: (r) => (
              <Badge variant="outline" className="font-mono text-[10px] capitalize">
                {r.cadence}
              </Badge>
            ),
            align: 'right',
            className: 'w-28',
          },
          {
            key: 'when',
            header: 'When',
            cell: (r) => (
              <span className="text-xs text-muted-foreground">
                {r.delivered_at
                  ? `delivered ${relTime(r.delivered_at)}`
                  : relTime(r.created_at)}
              </span>
            ),
            align: 'right',
            className: 'w-36',
          },
          {
            key: 'open',
            header: '',
            cell: (r) => (
              <Link
                to="/reports/$reportId"
                params={{ reportId: r.id }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Open →
              </Link>
            ),
            align: 'right',
            className: 'w-20',
          },
        ]}
        emptyState={
          <EmptyState
            icon={LineChart}
            title="See how your SEO is doing"
            description="Generate a report for any project to get a plain-English summary of traffic, rankings, and what changed."
            action={<ReportCreateButton projects={availableProjects} />}
          />
        }
      />
    </Page>
  );
}
